<div class="mx-auto max-w-(--breakpoint-2xl) space-y-6">
  <div class="flex flex-col gap-2">
    <a routerLink="/reports" class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-600">
      <lucide-icon [name]="ArrowLeftIcon" [size]="16"></lucide-icon>
      Back to reports
    </a>
    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Create {{ reportTitle }} Report</h1>
  </div>

  @if (step === 'filters') {
    <div class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div class="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Venues</h2>
          <p class="text-sm text-gray-500">Choose one or more venues. Leave empty for all.</p>
          <div class="mt-3 flex flex-wrap gap-2">
            <button type="button" class="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium" (click)="selectAllVenues()">Select all</button>
            <button type="button" class="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium" (click)="clearVenues()">Clear</button>
          </div>
          <div class="mt-4 grid gap-3 sm:grid-cols-2">
            @for (venue of venues; track venue.id) {
              <label class="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  [checked]="form.controls.venue_ids.value.includes(venue.id ?? 0)"
                  (change)="toggleVenue(venue.id ?? 0)"
                />
                <span>{{ venue.name }}</span>
              </label>
            }
          </div>
        </div>

        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Date range</h2>
          <p class="text-sm text-gray-500">Choose a preset or set custom dates.</p>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-full border px-3 py-1 text-xs font-medium"
              [ngClass]="selectedPreset === 'week' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'"
              (click)="setPreset('week')"
            >
              This week
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1 text-xs font-medium"
              [ngClass]="selectedPreset === 'month' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'"
              (click)="setPreset('month')"
            >
              This month
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1 text-xs font-medium"
              [ngClass]="selectedPreset === 'quarter' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'"
              (click)="setPreset('quarter')"
            >
              This quarter
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1 text-xs font-medium"
              [ngClass]="selectedPreset === 'year' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'"
              (click)="setPreset('year')"
            >
              This year
            </button>
            <button
              type="button"
              class="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600"
              (click)="clearPreset()"
            >
              Custom
            </button>
          </div>
          <div class="mt-4 grid gap-4 md:grid-cols-2">
            <app-date-picker [control]="form.controls.date_from" [name]="'date_from'" [label]="'Date From'"></app-date-picker>
            <app-date-picker [control]="form.controls.date_to" [name]="'date_to'" [label]="'Date To'"></app-date-picker>
          </div>
        </div>

        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Event Status</h2>
          @if (isConfirmedOnlyReport) {
            <p class="text-sm text-gray-500">This report always uses confirmed events only.</p>
            <div class="mt-4">
              <span class="inline-flex items-center rounded-full border border-success-200 bg-success-50 px-3 py-1 text-sm font-medium text-success-700">
                Confirmed
              </span>
            </div>
          } @else {
            <p class="text-sm text-gray-500">Optional filter for event statuses.</p>
            <div class="mt-4 flex flex-wrap gap-3">
              @for (status of statusOptions; track status) {
                <label class="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    [checked]="form.controls.statuses.value.includes(status)"
                    (change)="toggleStatus(status)"
                  />
                  <span>{{ status }}</span>
                </label>
              }
            </div>
          }
        </div>
      </div>

      <div class="mt-8 flex justify-end">
        <app-button [label]="'Continue to Preview'" (click)="continueToPreview()"></app-button>
      </div>
    </div>
  }

  @if (step === 'confirm') {
    <div class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div class="flex items-center gap-3">
        <lucide-icon [name]="CheckIcon" [size]="20" class="text-success-500"></lucide-icon>
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Confirm report</h2>
          <p class="text-sm text-gray-500">This report will include {{ previewCount ?? 0 }} events.</p>
        </div>
      </div>

      <div class="mt-6 flex flex-wrap gap-4 text-sm text-gray-600">
        <div><strong>Venues:</strong> {{ form.controls.venue_ids.value.length ? form.controls.venue_ids.value.length + ' selected' : 'All' }}</div>
        <div><strong>Date range:</strong> {{ form.controls.date_from.value || 'Any' }} → {{ form.controls.date_to.value || 'Any' }}</div>
        <div><strong>Status:</strong> {{ form.controls.statuses.value.length ? form.controls.statuses.value.join(', ') : 'Any' }}</div>
      </div>

      <div class="mt-6 flex justify-between">
        <app-button [label]="'Back'" [style]="'secondary'" (click)="backToFilters()"></app-button>
        <app-button [label]="'Generate Report'" (click)="generateReport()"></app-button>
      </div>
    </div>
  }
</div>
